import { useId } from 'react';
import { CalendarDays, Clock3, Infinity as InfinityIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatPassExpiry, isPassExpiryValid, localDateTime, PASS_DURATION_PRESETS, previewPassExpiry, type PassExpiryDraft } from '@/lib/pass-expiry';

export { createPassExpiryDraft, isPassExpiryValid, toPassExpiryPayload, type PassExpiryDraft } from '@/lib/pass-expiry';

type Props = {
  value: PassExpiryDraft;
  onChange: (value: PassExpiryDraft) => void;
  disabled?: boolean;
  className?: string;
  durationMode?: 'extend' | 'from-now';
  showSummary?: boolean;
};

const modes = [
  { value: 'duration', label: 'На срок', icon: Clock3 },
  { value: 'absolute', label: 'До даты', icon: CalendarDays },
  { value: 'infinite', label: 'Бессрочно', icon: InfinityIcon },
] as const;

export function PassExpiryPicker({ value, onChange, disabled = false, className, durationMode = 'extend', showSummary = true }: Props) {
  const id = useId();
  const [datePart = '', timePart = ''] = value.expiresAt.split('T');
  const valid = isPassExpiryValid(value);
  const preview = previewPassExpiry(value);
  const preset = value.durationPreset === 'custom' ? 'custom'
    : PASS_DURATION_PRESETS.find(preset => preset.days === value.durationDays && value.durationHours === 0)?.value ?? 'custom';
  const patch = (next: Partial<PassExpiryDraft>) => onChange({ ...value, ...next });
  return <div className={cn('space-y-4', className)}>
    <fieldset disabled={disabled}>
      <legend className="mb-2 text-sm font-medium text-eol-white">Срок действия</legend>
      <div className="grid grid-cols-3 gap-1 rounded-xl border border-white/10 bg-eol-black/50 p-1">
        {modes.map(({ value: mode, label, icon: Icon }) => <label key={mode} className="min-w-0 cursor-pointer">
          <input type="radio" name={`pass-expiry-${id}`} value={mode} checked={value.mode === mode} onChange={() => patch({ mode })} className="peer sr-only" />
          <span className="flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-1 text-xs font-semibold text-eol-desc transition peer-checked:bg-eol-pink/10 peer-checked:text-eol-pink peer-focus-visible:ring-2 peer-focus-visible:ring-eol-pink/50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 sm:text-sm"><Icon className="h-3.5 w-3.5 shrink-0" />{label}</span>
        </label>)}
      </div>
    </fieldset>

    {value.mode === 'duration' && <div className="space-y-3">
      <label className="block"><span className="sr-only">Период проходки</span><Select value={preset} disabled={disabled} onChange={event => {
        const selected = PASS_DURATION_PRESETS.find(preset => preset.value === event.target.value);
        patch(selected ? { durationDays: selected.days, durationHours: 0, durationPreset: selected.value } : { durationPreset: 'custom' });
      }}>{PASS_DURATION_PRESETS.map(preset => <option key={preset.value} value={preset.value}>{preset.label}</option>)}<option value="custom">Свой срок…</option></Select></label>
      {preset === 'custom' && <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="mb-1.5 block text-xs text-eol-desc">Дни</span><Input type="number" min={0} max={3650} step={1} disabled={disabled} value={Number.isFinite(value.durationDays) ? value.durationDays : ''} onChange={event => patch({ durationDays: event.target.valueAsNumber })} /></label>
        <label className="block"><span className="mb-1.5 block text-xs text-eol-desc">Часы</span><Input type="number" min={0} max={23} step={1} disabled={disabled} value={Number.isFinite(value.durationHours) ? value.durationHours : ''} onChange={event => patch({ durationHours: event.target.valueAsNumber })} /></label>
      </div>}
      {!valid && <p className="text-xs text-eol-red" role="alert">Укажите целые дни от 0 до 3650 и часы от 0 до 23. Срок должен быть больше нуля.</p>}
    </div>}

    {value.mode === 'absolute' && <div className="space-y-2">
      <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-3">
        <label className="min-w-0"><span className="mb-1.5 block text-xs text-eol-desc">Дата окончания</span><Input type="date" min={localDateTime(new Date()).slice(0, 10)} value={datePart} disabled={disabled} onChange={event => patch({ expiresAt: `${event.target.value}T${timePart}` })} className="min-w-0 [color-scheme:dark]" /></label>
        <label className="min-w-0"><span className="mb-1.5 block text-xs text-eol-desc">Время</span><Input type="time" value={timePart} disabled={disabled} onChange={event => patch({ expiresAt: `${datePart}T${event.target.value}` })} className="min-w-0 [color-scheme:dark]" /></label>
      </div>
      <p className="text-xs text-eol-desc">Ваш часовой пояс: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
      {!valid && <p className="text-xs text-eol-red" role="alert">Укажите дату и время в будущем.</p>}
    </div>}

    {value.mode === 'infinite' && <p className="text-sm leading-6 text-eol-desc">Доступ без даты окончания. Проходку можно будет отозвать вручную.</p>}

    {showSummary && valid && value.mode !== 'infinite' && <div className="rounded-lg border border-white/10 bg-eol-black/30 p-3 text-xs leading-6 text-eol-desc" aria-live="polite">
      {value.mode === 'duration' && durationMode === 'extend'
        ? 'Срок добавится к действующей проходке. Если её нет или она истекла — отсчёт начнётся сейчас.'
        : <>Доступ до <strong className="font-medium text-eol-white">{formatPassExpiry(preview!)}</strong></>}
    </div>}
  </div>;
}
